'use client';

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Callout, Spinner } from '@galapagos/design-system/web';
import { FormActions } from '../../components/FormActions';
import { SignupShell } from '../../components/SignupShell';
import { StepStatusRegion } from '../../components/StepStatusRegion';
import { useSignupApi } from '../../hooks/useSignupApi';
import { useSignupNavigation } from '../../hooks/useSignupNavigation';
import { useStepHeading } from '../../hooks/useStepHeading';
import { recordAppLanguageDefault } from '../../wrap-up/app-language-default';
import { firstWrapUpStep } from '../../wrap-up/branching';
import { useOnboardingFinalize } from '../../wrap-up/useOnboardingFinalize';
import { useWrapUpContext } from '../../wrap-up/useWrapUpContext';
import { ensureWrapUpStyle } from '../../wrap-up/wrap-up-style';
import { gated } from './gated';

/**
 * `/wrap-up/`: no content of its own. It finalizes the member's intake
 * session, then reads the HDHP notice, and once that and the sponsor read
 * answer, replaces itself with the first wrap-up screen that applies. A finalize that keeps
 * failing holds the member here with Try again.
 */
function WrapUpRouterStep() {
  ensureWrapUpStyle();
  const { t, i18n } = useTranslation('signup');
  const api = useSignupApi();
  const loading = t('wrapUp.router.loading');
  const headingRef = useStepHeading(loading);
  const finalize = useOnboardingFinalize();
  // The finalize enrolls the member's main program, and the HDHP notice is
  // derived from that program, so the notice is only asked for once it lands.
  const state = useWrapUpContext({ hdhpNoticeReady: finalize.status === 'done' });
  const { goToStep } = useSignupNavigation();
  const moved = useRef(false);
  useEffect(() => {
    if (!state.ready || finalize.status !== 'done' || moved.current) return;
    moved.current = true;
    recordAppLanguageDefault(api, state.context, i18n.language);
    goToStep(firstWrapUpStep(state.context));
  }, [state, finalize.status, goToStep, api, i18n.language]);
  const failed = finalize.status === 'failed';
  return (
    <SignupShell>
      <h1 ref={headingRef} tabIndex={-1} className="signup-visually-hidden">
        {loading}
      </h1>
      {/* One wrapper in both states, so the live region below never remounts. */}
      <div className="signup-wrap-up__loading" data-state={failed ? 'failed' : 'loading'}>
        {failed ? null : (
          // The heading and the status line already say this; the spinner is for sight only.
          <div aria-hidden="true">
            <Spinner accessibilityLabel={loading} data-testid="signup-wrap-up-spinner" />
          </div>
        )}
        <StepStatusRegion
          message={failed ? t('wrapUp.router.saveError') : loading}
          tone={failed ? 'error' : 'neutral'}
        />
      </div>
      {failed ? (
        <>
          {/* The status region above announces the error; this copy is for sight only. */}
          <div aria-hidden="true">
            <Callout tone="error" body={t('wrapUp.router.saveError')} />
          </div>
          <FormActions>
            <Button
              size="L"
              type="Primary"
              label={t('wrapUp.router.retry')}
              actionId="signup.wrap-up-retry.activate"
              data-testid="signup-wrap-up-retry"
              onPress={finalize.retry}
            />
          </FormActions>
        </>
      ) : null}
    </SignupShell>
  );
}

export const WrapUpRouterScreen = gated(WrapUpRouterStep, 'wrap-up');
